import { Injectable, InjectionToken, Optional, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Scheduler } from 'rxjs';
import { async } from 'rxjs/internal/scheduler/async';
import { debounceTime, switchMap, map, catchError, mergeMap, tap } from 'rxjs';
import { Action } from '@ngrx/store';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { User } from './../models/user';
import { AuthService } from './../services/auth.service';
import * as authActions from '../../auth/actions/auth';
import * as withSoftLabelActions from '../../soft-labels/actions/soft-labels';
import * as withUserSettingsActions from '../../user-settings/actions/user-settings';
import { OidcAuthService } from '../services/oidc-auth.service';

export const SEARCH_DEBOUNCE = new InjectionToken<number>('Search Debounce');
export const SEARCH_SCHEDULER = new InjectionToken<Scheduler>(
  'Search Scheduler'
);

@Injectable()
export class AuthEffects {

  
  login$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<authActions.Login>(authActions.LOGIN),
    debounceTime(this.debounce, this.scheduler || async),
    switchMap(action => {
      return this.authService.login(action.payload.authenticate).pipe(
        map((user: User) => {
          this.router.navigate([action.payload.returnUrl]);
          return new authActions.LoginSuccess({ user });
        }),
        catchError(exception => {
          let isInvalidGrant = false;
          if (exception.status === 400) {
            const firstError = exception.error;
            if (firstError && firstError.error === 'invalid_grant' && firstError.error_description === 'The user name or password is incorrect.') {
              console.log('invalid_grant returned from /Token Endpoint');
              isInvalidGrant = true;
            }
          }
          return of(new authActions.LoginFailure({ exception, isInvalidGrant }));
        })
      );
    })));

  
  loginSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<authActions.LoginSuccess>(authActions.LOGIN_SUCCESS),
    mergeMap(() => [new withSoftLabelActions.Load(), new withUserSettingsActions.Load()])
  ));

  
  loginRedirect$ = createEffect(() => this.actions$.pipe(
    ofType<authActions.LoginRedirect>(authActions.LOGIN_REDIRECT),
    tap((action: authActions.LoginRedirect) => {
      console.warn("LoginRedirect is deprecated");
      //this.router.navigate(['/login'], { queryParams: { returnUrl: action.payload.returnUrl } })
    })
  ), { dispatch: false });

  
  logoff$ = createEffect(() => this.actions$.pipe(
    ofType(authActions.LOGOUT),
    tap((action: authActions.Logout) => {
      this.oidcAuthService.logout();
    })
  ), { dispatch: false });

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private router: Router,
    @Optional()
    @Inject(SEARCH_DEBOUNCE)
    private debounce: number = 300,
    @Optional()
    @Inject(SEARCH_SCHEDULER)
    private scheduler: Scheduler,
    private readonly oidcAuthService: OidcAuthService
  ) { }

}
